import { cx } from './cx';
import { QrCode } from './QrCode';
import styles from './QrPanel.module.css';

/** Smallest QR edge that still scans from a phone across a living room at 1080p. */
export const QR_PANEL_MIN_SIZE = 180;

export interface QrPanelProps {
  /** What the QR encodes (the guest join / search URL). */
  value: string;
  /** Bold first line ("Busca desde tu celular"); optional where space is tight. */
  title?: string;
  /** The instruction ("Escanea el código para buscar y mandar canciones…"). */
  body: string;
  /** QR edge in px, quiet zone included. Never below `QR_PANEL_MIN_SIZE`. */
  size?: number;
  /** `card`: on its own flat surface (Search, Home). `bare`: inside a surface already (rail). */
  tone?: 'card' | 'bare';
  className?: string;
  'data-testid'?: string;
}

/**
 * "Send songs from your phone": a scannable QR with its instructions beside it — the one
 * component Search, the Home footer and the expanded rail all use. It is a read-out: it never
 * registers with the spatial navigation, so it can never take the focus.
 *
 * The QR is a fixed square (`QrCode`): white, high-contrast SVG with a two-module quiet zone,
 * never shrunk by the text next to it.
 */
export function QrPanel({
  value,
  title,
  body,
  size = 200,
  tone = 'card',
  className,
  ...rest
}: QrPanelProps) {
  const edge = Math.max(QR_PANEL_MIN_SIZE, size);
  return (
    <div
      className={cx(styles.panel, tone === 'card' && styles.card, className)}
      data-testid={rest['data-testid'] ?? 'qr-panel'}
      aria-live="off"
    >
      <QrCode value={value} size={edge} margin={2} />
      <div className={styles.text}>
        {title ? <div className={styles.title}>{title}</div> : null}
        <div className={styles.body}>{body}</div>
      </div>
    </div>
  );
}
